import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  Award,
  Building2,
  CalendarRange,
  CheckCircle2,
  ChevronRight,
  Globe,
  Loader2,
  Mail,
  MapPin,
  Paperclip,
  Phone,
  Plane,
  Send,
  ShieldCheck,
  Star,
  Stethoscope,
  TrendingUp,
  UserRoundPlus,
  Users,
  X,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes } from 'firebase/storage';
import { staffingFunctions, staffingStorage } from '../firebase';
import lumosyeLogo from '../assets/Lumosye-staffing.png';

/* ─────────────────── CONTACT CONSTANTS ────────────────────────────── */
const PHONE_VANITY = '(877) 77-LOCUM';
const PHONE_HREF = 'tel:+18777756286';
const CELL_DISPLAY = '(346) 667-7876';
const CELL_HREF = 'tel:+13466677876';
const EMAIL = 'contact@lumosye.com';
const MIN_FILL_TIME_MS = 4000;

/* ─────────────────── FORM PRIMITIVES ──────────────────────────────── */
const iCls =
  'w-full rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#c9a84c]/60 focus:ring-1 focus:ring-[#c9a84c]/30 transition';
const sCls =
  'w-full rounded-xl border border-[#1e2d4a] bg-[#070f1f] px-4 py-3 text-sm text-white focus:outline-none focus:border-[#c9a84c]/60 focus:ring-1 focus:ring-[#c9a84c]/30 transition appearance-none';

function Field({ label, required, hint, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-400">
        {label}
        {required && <span className="text-[#c9a84c] ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

/* ─────────────────── MODAL ─────────────────────────────────────────── */
function InquiryModal({ initialTab, onClose }) {
  const [tab, setTab] = useState(initialTab || 'hospital');
  const [form, setForm] = useState({});
  const [status, setStatus] = useState('idle');
  const [cvFile, setCvFile] = useState(null);
  const fileInputRef = useRef(null);
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    setForm({});
    setStatus('idle');
    setCvFile(null);
    startedAtRef.current = Date.now();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [tab]);

  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    const fillDurationMs = Date.now() - startedAtRef.current;
    const honeypotFilled = Boolean(form.website);

    if (honeypotFilled || fillDurationMs < MIN_FILL_TIME_MS) {
      setStatus('success');
      setForm({});
      setCvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      let cvPath = '';
      let cvFileName = '';

      if (cvFile) {
        const tempId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const storageRef = ref(staffingStorage, `cvs/${tempId}/${cvFile.name}`);
        const snapshot = await uploadBytes(storageRef, cvFile);
        cvPath = snapshot.ref.fullPath;
        cvFileName = cvFile.name;
      }

      const submitStaffingInquiry = httpsCallable(
        staffingFunctions,
        'submitStaffingInquiry'
      );

      await submitStaffingInquiry({
        type: tab,
        ...form,
        fillDurationMs,
        website: form.website || '',
        cvPath,
        cvFileName,
      });

      setStatus('success');
      setForm({});
      setCvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error('Submission error:', err);
      setStatus('error');
    }
  };

  const TABS = [
    { id: 'hospital', label: 'Hospital' },
    { id: 'clinician', label: 'Clinician' },
    { id: 'general', label: 'General' },
  ];

  const TITLES = {
    hospital: 'Request Hospital Coverage',
    clinician: 'Quick Clinician Inquiry',
    general: 'Get In Touch',
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(2,6,18,0.88)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-3xl border border-[#1e2d4a] bg-[#0b1628] shadow-2xl shadow-black/70">
        <div className="h-0.5 w-full bg-gradient-to-r from-[#c9a84c]/0 via-[#c9a84c] to-[#c9a84c]/0 rounded-t-3xl" />

        <div className="flex items-start justify-between px-8 pt-7 pb-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#c9a84c] mb-1">
              Lumosye Medical Staffing
            </p>
            <h2 className="text-xl font-black text-white">{TITLES[tab]}</h2>
          </div>
          <button
            onClick={onClose}
            className="mt-0.5 p-2 rounded-xl text-slate-500 hover:text-white hover:bg-[#1e2d4a] transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex gap-1.5 px-8 mb-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex-1 rounded-xl py-2 text-xs font-bold uppercase tracking-widest transition ${
                tab === t.id
                  ? 'bg-[#c9a84c] text-[#040d1c]'
                  : 'border border-[#1e2d4a] text-slate-400 hover:border-[#c9a84c]/40 hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {status === 'success' ? (
          <div className="px-8 pb-10 text-center">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#c9a84c]/10 border border-[#c9a84c]/30 mb-5">
              <CheckCircle2 size={30} className="text-[#c9a84c]" />
            </div>
            <h3 className="text-xl font-black text-white mb-2">
              {tab === 'hospital' ? 'Request received' : 'Inquiry received'}
            </h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              {tab === 'hospital'
                ? "We'll be in touch within 48 hours to discuss coverage options."
                : tab === 'clinician'
                ? "We'll review your information and reach out about available assignments."
                : "We'll get back to you shortly."}
            </p>
            {tab === 'clinician' && (
              <p className="text-xs text-slate-500 mb-4">
                Ready to submit a full application?{' '}
                <Link to="/apply" onClick={onClose} className="text-[#c9a84c] hover:underline">
                  Apply to join our network →
                </Link>
              </p>
            )}
            <p className="text-xs text-slate-500 mb-6">
              Questions?{' '}
              <a href={`mailto:${EMAIL}`} className="text-[#c9a84c] hover:underline">
                {EMAIL}
              </a>
            </p>
            <button
              onClick={onClose}
              className="rounded-full border border-[#1e2d4a] px-7 py-2.5 text-sm font-semibold text-white hover:border-[#c9a84c]/40 transition"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-8 pb-8 flex flex-col gap-5">
            {/* Honeypot */}
            <div
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
              aria-hidden="true"
            >
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                type="text"
                tabIndex="-1"
                autoComplete="off"
                value={form.website || ''}
                onChange={(e) => set('website', e.target.value)}
              />
            </div>

            {/* ── HOSPITAL FORM ── */}
            {tab === 'hospital' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="First Name" required>
                    <input className={iCls} placeholder="Jane" required
                      value={form.firstName || ''} onChange={(e) => set('firstName', e.target.value)} />
                  </Field>
                  <Field label="Last Name" required>
                    <input className={iCls} placeholder="Smith" required
                      value={form.lastName || ''} onChange={(e) => set('lastName', e.target.value)} />
                  </Field>
                </div>
                <Field label="Title / Role">
                  <input className={iCls} placeholder="CMO, Medical Director, etc."
                    value={form.title || ''} onChange={(e) => set('title', e.target.value)} />
                </Field>
                <Field label="Hospital / Facility Name" required>
                  <input className={iCls} placeholder="Memorial Regional Hospital" required
                    value={form.facility || ''} onChange={(e) => set('facility', e.target.value)} />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="City" required>
                    <input className={iCls} placeholder="Houston" required
                      value={form.city || ''} onChange={(e) => set('city', e.target.value)} />
                  </Field>
                  <Field label="State" required>
                    <input className={iCls} placeholder="TX" required
                      value={form.state || ''} onChange={(e) => set('state', e.target.value)} />
                  </Field>
                </div>
                <Field label="Coverage Type Needed" required>
                  <select className={sCls} required
                    value={form.coverageType || ''} onChange={(e) => set('coverageType', e.target.value)}>
                    <option value="" disabled>Select coverage type</option>
                    <option>Hospitalist – Day Shifts</option>
                    <option>Hospitalist – Night / Nocturnist</option>
                    <option>Emergency Medicine</option>
                    <option>Advanced Practice (NP / PA)</option>
                    <option>Block Schedule</option>
                    <option>Short-Notice / Urgent</option>
                    <option>Other</option>
                  </select>
                </Field>
                <Field label="Estimated Start Date">
                  <input type="date" className={iCls}
                    value={form.startDate || ''} onChange={(e) => set('startDate', e.target.value)} />
                </Field>
                <Field label="Work Email" required>
                  <input type="email" className={iCls} placeholder="jane@hospital.org" required
                    value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
                </Field>
                <Field label="Phone">
                  <input type="tel" className={iCls} placeholder="(713) 000-0000"
                    value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} />
                </Field>
                <Field label="Additional Details">
                  <textarea rows={3} className={iCls}
                    placeholder="Number of shifts, census info, timeline, or anything else helpful…"
                    value={form.details || ''} onChange={(e) => set('details', e.target.value)} />
                </Field>
              </>
            )}

            {/* ── CLINICIAN QUICK INQUIRY ── */}
            {tab === 'clinician' && (
              <>
                {/* Nudge toward full application */}
                <div className="rounded-xl border border-[#c9a84c]/20 bg-[#c9a84c]/5 px-4 py-3 flex items-start gap-3">
                  <Award size={15} className="text-[#c9a84c] shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-300 leading-relaxed">
                    Want to formally join our network?{' '}
                    <Link to="/apply" onClick={onClose} className="text-[#c9a84c] font-semibold hover:underline">
                      Submit a full application →
                    </Link>
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="First Name" required>
                    <input className={iCls} placeholder="James" required
                      value={form.firstName || ''} onChange={(e) => set('firstName', e.target.value)} />
                  </Field>
                  <Field label="Last Name" required>
                    <input className={iCls} placeholder="Osei" required
                      value={form.lastName || ''} onChange={(e) => set('lastName', e.target.value)} />
                  </Field>
                </div>
                <Field label="Credential / Degree" required>
                  <select className={sCls} required
                    value={form.credential || ''} onChange={(e) => set('credential', e.target.value)}>
                    <option value="" disabled>Select your credential</option>
                    <option>MD</option>
                    <option>DO</option>
                    <option>NP</option>
                    <option>PA</option>
                    <option>Other</option>
                  </select>
                </Field>
                <Field label="Specialty" required>
                  <select className={sCls} required
                    value={form.specialty || ''} onChange={(e) => set('specialty', e.target.value)}>
                    <option value="" disabled>Select specialty</option>
                    <option>Hospital Medicine (Hospitalist)</option>
                    <option>Emergency Medicine</option>
                    <option>Internal Medicine</option>
                    <option>Family Medicine</option>
                    <option>Critical Care / ICU</option>
                    <option>Other</option>
                  </select>
                </Field>
                <Field label="States Licensed" required hint="List all states where you hold an active license">
                  <input className={iCls} placeholder="TX, AZ, IL…" required
                    value={form.licensedStates || ''} onChange={(e) => set('licensedStates', e.target.value)} />
                </Field>
                <Field label="Availability" required>
                  <select className={sCls} required
                    value={form.availability || ''} onChange={(e) => set('availability', e.target.value)}>
                    <option value="" disabled>Select availability</option>
                    <option>Immediately available</option>
                    <option>Within 30 days</option>
                    <option>1–3 months</option>
                    <option>Flexible / exploring options</option>
                  </select>
                </Field>
                <Field label="Assignment Preference">
                  <select className={sCls}
                    value={form.assignmentPref || ''} onChange={(e) => set('assignmentPref', e.target.value)}>
                    <option value="" disabled>Select preference</option>
                    <option>Block schedules</option>
                    <option>Day shifts</option>
                    <option>Nights / Nocturnist</option>
                    <option>Short-notice / flexible</option>
                    <option>Open to all</option>
                  </select>
                </Field>
                <Field label="Email" required>
                  <input type="email" className={iCls} placeholder="you@email.com" required
                    value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
                </Field>
                <Field label="Phone">
                  <input type="tel" className={iCls} placeholder="(713) 000-0000"
                    value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} />
                </Field>
                <Field label="Anything else we should know?">
                  <textarea rows={3} className={iCls}
                    placeholder="Board certifications, geographic preferences, CV notes…"
                    value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />
                </Field>
                <Field label="Upload CV / Resume" hint="Optional · PDF or Word (.docx) · Max 10MB">
                  <label className={`flex items-center gap-3 cursor-pointer rounded-xl border border-dashed px-4 py-3 transition ${
                    cvFile ? 'border-[#c9a84c]/60 bg-[#c9a84c]/5' : 'border-[#1e2d4a] hover:border-[#c9a84c]/40 bg-[#070f1f]'
                  }`}>
                    <Paperclip size={15} className={cvFile ? 'text-[#c9a84c]' : 'text-slate-500'} />
                    <span className={`text-sm truncate ${cvFile ? 'text-[#c9a84c]' : 'text-slate-500'}`}>
                      {cvFile ? cvFile.name : 'Choose file or drag here'}
                    </span>
                    {cvFile && (
                      <button type="button"
                        onClick={(e) => { e.preventDefault(); setCvFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        className="ml-auto text-slate-500 hover:text-white transition">
                        <X size={14} />
                      </button>
                    )}
                    <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f && f.size <= 10 * 1024 * 1024) setCvFile(f);
                        else if (f) alert('File exceeds 10MB limit. Please upload a smaller file.');
                      }} />
                  </label>
                </Field>
              </>
            )}

            {/* ── GENERAL FORM ── */}
            {tab === 'general' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="First Name" required>
                    <input className={iCls} placeholder="Alex" required
                      value={form.firstName || ''} onChange={(e) => set('firstName', e.target.value)} />
                  </Field>
                  <Field label="Last Name" required>
                    <input className={iCls} placeholder="Johnson" required
                      value={form.lastName || ''} onChange={(e) => set('lastName', e.target.value)} />
                  </Field>
                </div>
                <Field label="Email" required>
                  <input type="email" className={iCls} placeholder="you@email.com" required
                    value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
                </Field>
                <Field label="Subject" required>
                  <select className={sCls} required
                    value={form.subject || ''} onChange={(e) => set('subject', e.target.value)}>
                    <option value="" disabled>Select a topic</option>
                    <option>Hospital staffing inquiry</option>
                    <option>Clinician / locum interest</option>
                    <option>Partnership or collaboration</option>
                    <option>Press / media</option>
                    <option>Other</option>
                  </select>
                </Field>
                <Field label="Message" required>
                  <textarea rows={4} className={iCls} placeholder="Tell us what's on your mind…" required
                    value={form.message || ''} onChange={(e) => set('message', e.target.value)} />
                </Field>
              </>
            )}

            {status === 'error' && (
              <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                Something went wrong. Please try again or email{' '}
                <a href={`mailto:${EMAIL}`} className="underline">{EMAIL}</a>.
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#c9a84c] py-3.5 text-sm font-bold text-[#040d1c] hover:bg-[#d9b95c] disabled:opacity-60 disabled:cursor-not-allowed transition shadow-lg shadow-[#c9a84c]/20"
            >
              {status === 'submitting'
                ? <><Loader2 size={16} className="animate-spin" /> Submitting…</>
                : <><Send size={15} /> Submit</>}
            </button>
            <p className="text-center text-xs text-slate-600">
              Your information is kept private and never sold or shared.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

/* ─────────────────── SMALL COMPONENTS ─────────────────────────────── */
function StatCard({ value, label, sub }) {
  return (
    <div className="flex flex-col gap-1 px-6 py-5 border-r border-[#1e2d4a] last:border-r-0">
      <span className="text-3xl md:text-4xl font-black text-white tracking-tight">{value}</span>
      <span className="text-sm font-semibold text-[#c9a84c] uppercase tracking-widest">{label}</span>
      {sub && <span className="text-xs text-slate-400 mt-0.5">{sub}</span>}
    </div>
  );
}

function SpecialtyCard({ icon, title, text, accent = false }) {
  return (
    <div className={`group relative rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1 ${
      accent
        ? 'border-[#c9a84c]/40 bg-gradient-to-br from-[#0b1628] to-[#0f1e35] hover:border-[#c9a84c]/70'
        : 'border-[#1e2d4a] bg-[#0b1628]/60 hover:border-[#c9a84c]/40'
    }`}>
      <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#c9a84c]/10 border border-[#c9a84c]/20">
        {icon}
      </div>
      <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
      <p className="text-slate-400 text-sm leading-relaxed">{text}</p>
      <div className="mt-5 flex items-center gap-1 text-xs font-semibold text-[#c9a84c] opacity-0 group-hover:opacity-100 transition-opacity">
        Learn more <ArrowUpRight size={12} />
      </div>
    </div>
  );
}

function ValuePill({ label }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-[#1e2d4a] bg-[#0b1628]/60 px-4 py-3">
      <CheckCircle2 size={15} className="text-[#c9a84c] shrink-0" />
      <span className="text-sm text-slate-300">{label}</span>
    </div>
  );
}

function TrustBadge({ icon, text, href }) {
  const cls = 'flex items-center gap-2 text-slate-400 text-sm hover:text-slate-300 transition';
  return href
    ? <a href={href} className={cls}><span className="text-[#c9a84c]">{icon}</span>{text}</a>
    : <span className={cls}><span className="text-[#c9a84c]">{icon}</span>{text}</span>;
}

/* ─────────────────── MAIN PAGE ─────────────────────────────────────── */
export default function MedicalStaffingLanding() {
  const [modal, setModal] = useState(null);
  const openModal = useCallback((type) => setModal(type), []);
  const closeModal = useCallback(() => setModal(null), []);

  useEffect(() => {
    document.body.style.overflow = modal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [modal]);

  return (
    <div className="min-h-screen text-white"
      style={{ background: 'linear-gradient(160deg, #040d1c 0%, #071325 50%, #050e1f 100%)' }}>

      <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.025]"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg,#ffffff 0px,#ffffff 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,#ffffff 0px,#ffffff 1px,transparent 1px,transparent 40px)' }} />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] pointer-events-none z-0"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(201,168,76,0.07) 0%, transparent 70%)' }} />

      {modal && <InquiryModal initialTab={modal} onClose={closeModal} />}

      {/* ── HEADER ── */}
      <header className="sticky top-0 z-50 border-b border-[#1e2d4a]/80 backdrop-blur-xl bg-[#040d1c]/80">
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between max-w-7xl">
          <Link to="/" className="flex items-center gap-3">
            <img src={lumosyeLogo} alt="Lumosye Health" className="h-10 w-auto object-contain transition-transform duration-300 hover:scale-[1.03]" />
            <span className="hidden sm:block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 border-l border-[#1e2d4a] pl-3">
              Medical Staffing
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-400">
            <a href="#specialties" className="hover:text-white transition">Specialties</a>
            <a href="#why-lumosye" className="hover:text-white transition">Why Lumosye</a>
            <a href="#for-clinicians" className="hover:text-white transition">Clinicians</a>
            <a href={PHONE_HREF} className="flex items-center gap-1.5 hover:text-white transition">
              <Phone size={13} />{PHONE_VANITY}
            </a>
          </div>
          <button onClick={() => openModal('hospital')}
            className="inline-flex items-center gap-2 rounded-full bg-[#c9a84c] px-5 py-2.5 text-sm font-bold text-[#040d1c] hover:bg-[#d9b95c] transition shadow-lg shadow-[#c9a84c]/20">
            Request Coverage <ArrowRight size={14} />
          </button>
        </nav>
      </header>

      {/* ── HERO ── */}
      <section className="relative z-10 pt-20 md:pt-28 pb-16 md:pb-24">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-[#c9a84c]" />
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a84c]">
              Lumosye Health · Medical Staffing Division
            </span>
          </div>
          <div className="max-w-5xl mb-10">
            <h1 className="text-5xl md:text-7xl xl:text-8xl font-black text-white leading-[0.9] tracking-tight mb-6"
              style={{ fontFamily: "'Georgia', serif" }}>
              Dependable Clinical
              <br /><span style={{ color: '#c9a84c' }}>Coverage,</span>
              <br /><span className="text-slate-300">Done Right.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl leading-relaxed">
              Premium locum tenens staffing for hospitals and health systems that need reliable
              physician and advanced practice coverage — without the friction of traditional agencies.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <button onClick={() => openModal('hospital')}
              className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#c9a84c] px-8 py-4 text-base font-bold text-[#040d1c] hover:bg-[#d9b95c] transition shadow-2xl shadow-[#c9a84c]/25">
              Request Hospital Coverage <ArrowRight size={16} />
            </button>
            <Link to="/apply"
              className="inline-flex items-center justify-center gap-2.5 rounded-full border border-[#1e2d4a] bg-[#0b1628]/60 px-8 py-4 text-base font-semibold text-white hover:border-[#c9a84c]/50 hover:bg-[#0b1628] transition">
              Apply to Join Network <ArrowRight size={16} />
            </Link>
          </div>
          <div className="rounded-2xl border border-[#1e2d4a] bg-[#0b1628]/60 overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 divide-[#1e2d4a]">
              <StatCard value="48h" label="Avg. Response" sub="From inquiry to placement" />
              <StatCard value="50" label="States" sub="Nationwide placement support" />
              <StatCard value="100%" label="ABIM Certified" sub="Board-certified physicians" />
              <StatCard value="24/7" label="Availability" sub="Day, night & nocturnist" />
            </div>
          </div>
        </div>
      </section>

      {/* ── COVERAGE BAND ── */}
      <section className="relative z-10 py-10 border-y border-[#1e2d4a] bg-[#0b1628]/40">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {['Hospitalist day & night coverage','Emergency medicine locum support',
              'NP / PA placement & clinical reinforcement','Short-term, block & recurring assignments',
              'Credentialing & malpractice coordination','Travel & lodging support when needed',
              'Licensing support & reimbursement for clinicians',
            ].map((item) => <ValuePill key={item} label={item} />)}
          </div>
        </div>
      </section>

      {/* ── SPECIALTIES ── */}
      <section id="specialties" className="relative z-10 py-24 md:py-32">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex items-end justify-between mb-14">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-8 bg-[#c9a84c]" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a84c]">
                  Coverage Specialties
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl font-black text-white leading-tight"
                style={{ fontFamily: "'Georgia', serif" }}>
                Built for the staffing<br />gaps hospitals actually face
              </h2>
            </div>
            <button onClick={() => openModal('general')}
              className="hidden lg:inline-flex items-center gap-2 text-sm text-[#c9a84c] font-semibold hover:underline">
              Discuss your needs <ArrowUpRight size={14} />
            </button>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            <SpecialtyCard accent icon={<Stethoscope className="text-[#c9a84c]" size={24} />}
              title="Hospital Medicine"
              text="Day shifts, nocturnist coverage, block schedules, and hospitalist staffing gaps — including short-notice placements." />
            <SpecialtyCard icon={<Building2 className="text-[#c9a84c]" size={24} />}
              title="Emergency Medicine"
              text="Locum EM coverage for hospitals navigating vacancies, throughput challenges, or flexible department support needs." />
            <SpecialtyCard icon={<UserRoundPlus className="text-[#c9a84c]" size={24} />}
              title="NP / PA Coverage"
              text="Advanced practice staffing that reinforces clinical teams and provides dependable continuity across shifts." />
            <SpecialtyCard icon={<CalendarRange className="text-[#c9a84c]" size={24} />}
              title="Block & Recurring"
              text="From urgent short-term placements to recurring locum blocks, assignments are structured around your program demand." />
            <SpecialtyCard icon={<ShieldCheck className="text-[#c9a84c]" size={24} />}
              title="Malpractice & Credentialing"
              text="Assignment support with malpractice coordination, credentialing documentation, and streamlined onboarding workflows." />
            <SpecialtyCard icon={<Plane className="text-[#c9a84c]" size={24} />}
              title="Travel Coordination"
              text="Travel and lodging support coordinated for clinicians covering regional or out-of-market facilities when needed." />
          </div>
        </div>
      </section>

      {/* ── WHY LUMOSYE ── */}
      <section id="why-lumosye" className="relative z-10 py-24 md:py-32 border-t border-[#1e2d4a]">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-8 bg-[#c9a84c]" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a84c]">
                  Why Lumosye
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-6"
                style={{ fontFamily: "'Georgia', serif" }}>
                A staffing experience<br />that actually works<br />for clinical teams
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Lumosye Medical Staffing was designed by a practicing hospitalist who has lived both
                sides of the locum equation. We build around the way real hospital programs operate
                — with clarity, responsiveness, and genuine alignment.
              </p>
              <button onClick={() => openModal('hospital')}
                className="inline-flex items-center gap-2.5 rounded-full bg-[#c9a84c] px-7 py-3.5 text-sm font-bold text-[#040d1c] hover:bg-[#d9b95c] transition shadow-lg shadow-[#c9a84c]/20">
                Partner with Lumosye <ArrowRight size={15} />
              </button>
            </div>
            <div className="space-y-4">
              {[
                { icon: <Award size={18} className="text-[#c9a84c]" />, title: 'Physician-run by design', body: 'Founded and led by a Yale-trained, ABIM-certified hospitalist in active practice. We understand coverage gaps from the inside — because we live them.' },
                { icon: <TrendingUp size={18} className="text-[#c9a84c]" />, title: 'Cleaner hospital communication', body: 'Clear assignment structure, responsive communication, and practical follow-through — no chasing, no ambiguity.' },
                { icon: <ShieldCheck size={18} className="text-[#c9a84c]" />, title: 'End-to-end placement support', body: 'Coverage details, malpractice coordination, and onboarding support handled in a single, streamlined process.' },
                { icon: <Star size={18} className="text-[#c9a84c]" />, title: 'Built for long-term trust', body: 'We want hospitals to see Lumosye as a dependable clinical partner — not just another staffing vendor they cycle through.' },
              ].map(({ icon, title, body }) => (
                <div key={title} className="flex gap-4 rounded-2xl border border-[#1e2d4a] bg-[#0b1628]/60 p-5 hover:border-[#c9a84c]/30 transition">
                  <div className="mt-0.5 h-8 w-8 shrink-0 flex items-center justify-center rounded-lg bg-[#c9a84c]/10 border border-[#c9a84c]/20">{icon}</div>
                  <div>
                    <p className="font-bold text-white text-sm mb-1">{title}</p>
                    <p className="text-slate-400 text-sm leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FOR HOSPITALS ── */}
      <section className="relative z-10 py-16 border-t border-[#1e2d4a]">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="rounded-3xl overflow-hidden border border-[#c9a84c]/20 bg-gradient-to-br from-[#0d1c35] to-[#0b1628]">
            <div className="grid lg:grid-cols-[1.2fr_0.8fr]">
              <div className="p-10 md:p-14 border-r border-[#1e2d4a]">
                <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-[#c9a84c] mb-5">
                  For Hospital Programs
                </span>
                <h2 className="text-2xl md:text-4xl font-black text-white leading-tight mb-5"
                  style={{ fontFamily: "'Georgia', serif" }}>
                  Coverage gaps cost more<br />than a locum placement.
                </h2>
                <p className="text-slate-400 leading-relaxed mb-8 max-w-lg">
                  Unplanned physician vacancies burden your permanent staff, affect throughput, and
                  compromise patient outcomes. Lumosye provides fast, credentialed, and dependable
                  coverage before those costs compound.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {['Fast response from inquiry to placement','Board-certified physicians and APPs',
                    'Flexible scheduling to match your census','Malpractice and credentialing handled',
                    'Nationwide placement support'].map((item) => (
                    <div key={item} className="flex items-start gap-2.5">
                      <CheckCircle2 size={14} className="text-[#c9a84c] mt-0.5 shrink-0" />
                      <span className="text-sm text-slate-300">{item}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => openModal('hospital')}
                  className="inline-flex items-center gap-2.5 rounded-full bg-[#c9a84c] px-7 py-3.5 text-sm font-bold text-[#040d1c] hover:bg-[#d9b95c] transition shadow-lg shadow-[#c9a84c]/20">
                  Request Coverage Now <ArrowRight size={15} />
                </button>
              </div>
              <div className="p-10 md:p-14 flex flex-col justify-between bg-[#070f1f]/40">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#c9a84c] mb-6">
                    What We Cover
                  </p>
                  <div className="space-y-3">
                    {['Hospitalist day & night shifts','Nocturnist programs','Emergency medicine',
                      'Advanced practice (NP/PA)','Block & recurring locum schedules',
                      'Short-notice urgent placements'].map((item) => (
                      <div key={item} className="flex items-center gap-3">
                        <ChevronRight size={14} className="text-[#c9a84c] shrink-0" />
                        <span className="text-sm text-slate-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-10 pt-8 border-t border-[#1e2d4a]">
                  <p className="text-xs text-slate-500 uppercase tracking-[0.2em] mb-3">Coverage Reach</p>
                  <div className="flex items-start gap-3 rounded-xl border border-[#c9a84c]/20 bg-[#c9a84c]/5 px-4 py-3">
                    <Globe size={15} className="text-[#c9a84c] shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-300 leading-relaxed">
                      Nationwide placement support — currently active in multiple states with
                      ongoing expansion across all 50 states.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOR CLINICIANS ── */}
      <section id="for-clinicians" className="relative z-10 py-24 md:py-28 border-t border-[#1e2d4a]">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="rounded-3xl border border-[#1e2d4a] bg-[#0b1628]/60 p-10 md:p-12">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#c9a84c]/10 border border-[#c9a84c]/20 mb-6">
                <Users className="text-[#c9a84c]" size={22} />
              </div>
              <h2 className="text-2xl md:text-4xl font-black text-white mb-4"
                style={{ fontFamily: "'Georgia', serif" }}>
                Join a locum network<br />built by a clinician
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                Lumosye was founded by a practicing hospitalist and nocturnist. Our network is
                designed around flexibility, clean onboarding, transparent communication, and
                assignments that actually fit your schedule.
              </p>
              <div className="space-y-2.5 mb-8">
                {['Flexible block and shift scheduling','Clear assignment details upfront',
                  'Malpractice coverage coordination','Licensing support & fee reimbursement',
                  'Travel and lodging support available','Physician-led, clinician-first culture',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2.5">
                    <CheckCircle2 size={14} className="text-[#c9a84c] shrink-0" />
                    <span className="text-sm text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/apply"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#c9a84c] px-6 py-3 text-sm font-bold text-[#040d1c] hover:bg-[#d9b95c] transition shadow-lg shadow-[#c9a84c]/20">
                  Apply Now <ArrowRight size={14} />
                </Link>
                <button onClick={() => openModal('clinician')}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#c9a84c]/40 bg-[#c9a84c]/10 px-6 py-3 text-sm font-semibold text-[#c9a84c] hover:bg-[#c9a84c]/20 transition">
                  Quick Inquiry
                </button>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-8 bg-[#c9a84c]" />
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a84c]">
                  Clinician-First
                </span>
              </div>
              <p className="text-3xl md:text-4xl font-black text-white leading-tight"
                style={{ fontFamily: "'Georgia', serif" }}>
                "We built what we<br />wished existed<br />as a locum ourselves."
              </p>
              <p className="text-slate-400 leading-relaxed">
                Lumosye is physician-run — founded and led by a Yale-trained, ABIM-certified
                hospitalist currently practicing in the Houston area. Our locum network reflects
                how a physician actually evaluates and accepts assignments: with honesty, clear
                expectations, and real support from someone who has been in your shoes.
              </p>
              <div className="flex items-center gap-3 pt-2 rounded-xl border border-[#c9a84c]/20 bg-[#c9a84c]/5 px-4 py-3">
                <Award size={16} className="text-[#c9a84c] shrink-0" />
                <p className="text-xs text-slate-300 leading-relaxed">
                  <span className="text-[#c9a84c] font-semibold">Physician-run.</span> Yale-trained ·
                  ABIM Board Certified · Active hospitalist & nocturnist practice
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="contact" className="relative z-10 py-24 md:py-32 border-t border-[#1e2d4a]">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-8 bg-[#c9a84c]" />
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c9a84c]">
              Get In Touch
            </span>
            <span className="h-px w-8 bg-[#c9a84c]" />
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-white mb-5 leading-tight"
            style={{ fontFamily: "'Georgia', serif" }}>
            Let's build dependable<br />
            <span style={{ color: '#c9a84c' }}>coverage together.</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-12">
            Whether you're a hospital program facing coverage gaps or a clinician exploring locum
            opportunities, we'd be glad to connect.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
            <button onClick={() => openModal('hospital')}
              className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#c9a84c] px-9 py-4 text-base font-bold text-[#040d1c] hover:bg-[#d9b95c] transition shadow-2xl shadow-[#c9a84c]/20">
              Request Hospital Coverage <ArrowRight size={16} />
            </button>
            <Link to="/apply"
              className="inline-flex items-center justify-center gap-2.5 rounded-full border border-[#1e2d4a] bg-[#0b1628]/60 px-9 py-4 text-base font-semibold text-white hover:border-[#c9a84c]/50 transition">
              Apply to Join Network <ArrowRight size={16} />
            </Link>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm mb-3">
            <TrustBadge icon={<Mail size={14} />} text={EMAIL} href={`mailto:${EMAIL}`} />
            <TrustBadge icon={<Phone size={14} />} text={`${PHONE_VANITY} (calls)`} href={PHONE_HREF} />
            <TrustBadge icon={<Phone size={14} />} text={`${CELL_DISPLAY} (text/call)`} href={CELL_HREF} />
            <TrustBadge icon={<MapPin size={14} />} text="Katy / The Woodlands, TX" />
          </div>
          <p className="text-xs text-slate-600">
            {PHONE_VANITY} accepts calls only · Text or email preferred for fastest response
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="relative z-10 border-t border-[#1e2d4a] py-8">
        <div className="container mx-auto px-6 max-w-7xl flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={lumosyeLogo} alt="Lumosye" className="h-8 w-auto opacity-60 transition-transform duration-300 hover:scale-[1.03]" />
            <span className="text-xs text-slate-600">Medical Staffing · A Lumosye Health Division</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-slate-600">
            <Link to="/" className="hover:text-slate-400 transition">Lumosye Health</Link>
            <Link to="/apply" className="hover:text-slate-400 transition">Clinician Apply</Link>
            <button onClick={() => openModal('general')} className="hover:text-slate-400 transition">Contact</button>
            <Link to="/staffing-dashboard" className="hover:text-slate-400 transition">Staff Login</Link>
            <a href={`mailto:${EMAIL}`} className="hover:text-slate-400 transition">{EMAIL}</a>
            <span>© {new Date().getFullYear()} Lumosye Health Services LLC</span>
          </div>
        </div>
      </footer>
    </div>
  );
}